import { useCallback, useEffect, useState } from 'react';

interface ServiceWorkerState {
  isUpdateAvailable: boolean;
  isOffline: boolean;
  registration: ServiceWorkerRegistration | null;
}

interface UseServiceWorkerReturn extends ServiceWorkerState {
  updateServiceWorker: () => void;
}

const CHECK_INTERVAL_MS = 60 * 1000; // Check for updates every 60 seconds

export const useServiceWorker = (): UseServiceWorkerReturn => {
  const [state, setState] = useState<ServiceWorkerState>({
    isUpdateAvailable: false,
    isOffline: !navigator.onLine,
    registration: null,
  });

  const updateServiceWorker = useCallback(() => {
    const { registration } = state;
    
    if (registration?.waiting) {
      // Tell the waiting service worker to skip waiting and activate
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Reload the page to get the new version
    window.location.reload();
  }, [state]);

  useEffect(() => {
    // Only register in production
    if (import.meta.env.DEV) {
      console.log('[useServiceWorker] Skipping registration in development mode');
      return;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('[useServiceWorker] Service workers not supported');
      return;
    }

    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        console.log('[useServiceWorker] Service worker registered:', registration.scope);

        setState((prev) => ({ ...prev, registration }));

        // Check if there's already a waiting worker (update available)
        if (registration.waiting) {
          console.log('[useServiceWorker] Update available (waiting worker found)');
          setState((prev) => ({ ...prev, isUpdateAvailable: true }));
        }

        // Listen for new service worker installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (!newWorker) return;

          console.log('[useServiceWorker] New service worker installing...');

          newWorker.addEventListener('statechange', () => {
            console.log('[useServiceWorker] New worker state:', newWorker.state);
            
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content is available, show update notification
              console.log('[useServiceWorker] New version available!');
              setState((prev) => ({ ...prev, isUpdateAvailable: true }));
            }
          });
        });

        // Periodically check for updates
        const checkForUpdates = () => {
          console.log('[useServiceWorker] Checking for updates...');
          registration.update().catch((err) => {
            console.warn('[useServiceWorker] Update check failed:', err);
          });
        };

        // Check for updates periodically
        const intervalId = setInterval(checkForUpdates, CHECK_INTERVAL_MS);

        // Also check when the page becomes visible again
        const handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            checkForUpdates();
          }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup
        return () => {
          clearInterval(intervalId);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
      } catch (error) {
        console.error('[useServiceWorker] Registration failed:', error);
      }
    };

    // Handle controller change (when skipWaiting is called)
    const handleControllerChange = () => {
      console.log('[useServiceWorker] Controller changed, reloading...');
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Register the service worker
    const cleanupPromise = registerServiceWorker();

    // Handle online/offline status
    const handleOnline = () => setState((prev) => ({ ...prev, isOffline: false }));
    const handleOffline = () => setState((prev) => ({ ...prev, isOffline: true }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      cleanupPromise?.then((cleanup) => cleanup?.());
    };
  }, []);

  return {
    ...state,
    updateServiceWorker,
  };
};

export default useServiceWorker;


